import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Input component (shadcn/ui style)
 *
 * A flexible text input component with variants and icon support.
 * Supports left/right icons, error states, and proper accessibility.
 *
 * @example
 * // Basic usage
 * <Input placeholder="Enter text..." />
 *
 * // With left icon
 * <Input leftIcon={<SearchIcon />} placeholder="Search..." />
 *
 * // Error state
 * <Input variant="error" placeholder="Invalid input" />
 *
 * // With wrapper for icons
 * <InputWrapper>
 *   <InputIcon position="left"><SearchIcon /></InputIcon>
 *   <Input className="pl-10" placeholder="Search..." />
 * </InputWrapper>
 */

const Input = React.forwardRef(
  ({ className, type = "text", variant = "default", ...props }, ref) => {
    const variantStyles = {
      default: "border-input focus-visible:ring-ring",
      error: "border-destructive focus-visible:ring-destructive",
    };

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground ring-offset-background",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          variantStyles[variant] || variantStyles.default,
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

/**
 * InputWrapper - Container for input with icons
 */
const InputWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("relative flex items-center", className)}
    {...props}
  />
));
InputWrapper.displayName = "InputWrapper";

/**
 * InputIcon - Icon container for input
 * @param {Object} props
 * @param {'left'|'right'} props.position - Position of the icon
 */
const InputIcon = React.forwardRef(
  ({ className, position = "left", ...props }, ref) => {
    const positionStyles = {
      left: "left-0 pl-3",
      right: "right-0 pr-3",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "absolute inset-y-0 flex items-center pointer-events-none text-muted-foreground",
          positionStyles[position],
          className
        )}
        {...props}
      />
    );
  }
);
InputIcon.displayName = "InputIcon";

/**
 * SearchInput - Pre-configured input with search icon and clear button
 */
const SearchInput = React.forwardRef(({ className, value, onChange, onClear, ...props }, ref) => {
  const hasValue = value && value.length > 0;

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      // Create a synthetic event to clear the value
      const syntheticEvent = {
        target: { value: "" },
        currentTarget: { value: "" },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <InputWrapper>
      <InputIcon position="left">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </InputIcon>
      <Input
        ref={ref}
        type="search"
        value={value}
        onChange={onChange}
        className={cn("pl-10", hasValue ? "pr-10" : "", className)}
        {...props}
      />
      {hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-0 pr-3 inset-y-0 flex items-center text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Clear search"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      )}
    </InputWrapper>
  );
});
SearchInput.displayName = "SearchInput";

export { Input, InputWrapper, InputIcon, SearchInput };
